import {
  useMemo,
  useState,
  useCallback
} from 'react';

/**
 * Generic multi-select helper for tables that need bulk actions
 * (e.g. delete/tag/recrawl across several selected rows).
 *
 * Selection is tracked by id and persists across pagination until
 * explicitly cleared, so users can select rows across pages.
 */
export function useBulkSelection() {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids) => {
    setSelectedIds(prev => {
      const allSelected = ids.length > 0 && ids.every(id => prev.has(id));
      const next = new Set(prev);
      ids.forEach(id => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

  const isAllSelected = useCallback(
    (ids) => ids.length > 0 && ids.every(id => selectedIds.has(id)),
    [selectedIds]
  );

  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  return {
    selectedIds: selectedArray,
    count: selectedIds.size,
    toggle,
    toggleAll,
    clear,
    isSelected,
    isAllSelected
  };
}

export default useBulkSelection;
